import { PaymentRecord } from '@/api/planApi';

const KNOWN_STATUSES = ['PENDING', 'CONFIRMED', 'CANCELLED', 'DONE'];
const KNOWN_PLANS = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];
const KNOWN_PAYMENT_METHODS = ['BANK_TRANSFER', 'CARD'];
const KNOWN_RECEIPT_TYPES = ['CASH_RECEIPT', 'TAX_INVOICE', 'NONE'];
const KNOWN_RECEIPT_ISSUE_STATUSES = ['NOT_ISSUED', 'PENDING', 'ISSUED', 'FAILED', 'CANCELLED'];

export type PaymentOverlayFields = Pick<
  PaymentRecord,
  | 'status'
  | 'amount'
  | 'paymentMethod'
  | 'billingMonth'
  | 'createdAt'
  | 'confirmedAt'
  | 'plan'
  | 'receiptType'
  | 'receiptIssueStatus'
  | 'receiptIssuedAt'
  | 'receiptIssueFailReason'
  | 'refundAmount'
  | 'refundedAt'
  | 'refundCompletedAt'
  | 'canCancel'
  | 'isDowngrade'
>;

function isNullableString(value: any): boolean {
  return value === null || typeof value === 'string';
}

function isNullableNumber(value: any): boolean {
  return value === null || (typeof value === 'number' && !Number.isNaN(value));
}

function isNullableBoolean(value: any): boolean {
  return value === null || typeof value === 'boolean';
}

function isValidRow(row: any, legacyIds: Set<number>): boolean {
  if (row == null || typeof row !== 'object') return false;
  if (typeof row.id !== 'number' || !legacyIds.has(row.id)) return false;
  if (typeof row.status !== 'string' || !KNOWN_STATUSES.includes(row.status)) return false;
  if (typeof row.plan !== 'string' || !KNOWN_PLANS.includes(row.plan)) return false;
  if (typeof row.amount !== 'number' || Number.isNaN(row.amount)) return false;
  if (typeof row.paymentMethod !== 'string' || !KNOWN_PAYMENT_METHODS.includes(row.paymentMethod)) return false;
  if (typeof row.billingMonth !== 'string') return false;
  if (typeof row.createdAt !== 'string') return false;
  if (!isNullableString(row.confirmedAt)) return false;
  if (typeof row.canCancel !== 'boolean') return false;
  if (!isNullableNumber(row.refundAmount)) return false;
  if (!isNullableString(row.refundedAt)) return false;
  if (!isNullableString(row.refundCompletedAt)) return false;
  if (row.receiptType !== null && !KNOWN_RECEIPT_TYPES.includes(row.receiptType)) return false;
  if (row.receiptIssueStatus !== null && !KNOWN_RECEIPT_ISSUE_STATUSES.includes(row.receiptIssueStatus)) return false;
  if (!isNullableString(row.receiptIssuedAt)) return false;
  if (!isNullableString(row.receiptIssueFailReason)) return false;
  if (!isNullableBoolean(row.isDowngrade)) return false;
  return true;
}

/**
 * workspaceApi.getMyBilling() 응답을 legacy records 위에 덮어씌울 overlay로 검증·변환한다.
 *
 * teamId/contractId/monthlyLimit/monthlyPrice/contractName/teamName/teamRole/
 * cashReceiptIssued는 절대 여기서 다루지 않는다(반환 객체에 명시적으로 나열한
 * 필드만 복사하므로, 응답에 이 필드들이 섞여 들어와도 overlay 결과로 새지 않음) -
 * 환불 API 라우팅(RefundConfirmDialog)과 환불/영수증 버튼 게이팅
 * (isPaymentSuperseded)이 이 필드들에 의존하기 때문에 legacy 값만 신뢰한다.
 *
 * all-or-nothing 검증: 배열이 아니거나, 페이지 내 행 개수가 legacy와 다르거나,
 * 행 하나라도 필드가 이상하면 전체를 버리고 빈 overlay({})를 반환한다.
 */
export function validateAndBuildOverlay(
  rows: unknown,
  records: PaymentRecord[]
): Record<number, PaymentOverlayFields> {
  if (!Array.isArray(rows)) return {};
  if (rows.length !== records.length) return {};

  const legacyIds = new Set(records.map((r) => r.id));
  const overlay: Record<number, PaymentOverlayFields> = {};

  for (const row of rows) {
    if (!isValidRow(row, legacyIds)) return {};
    overlay[row.id] = {
      status: row.status,
      amount: row.amount,
      paymentMethod: row.paymentMethod,
      billingMonth: row.billingMonth,
      createdAt: row.createdAt,
      confirmedAt: row.confirmedAt,
      plan: row.plan,
      receiptType: row.receiptType,
      receiptIssueStatus: row.receiptIssueStatus,
      receiptIssuedAt: row.receiptIssuedAt,
      receiptIssueFailReason: row.receiptIssueFailReason,
      refundAmount: row.refundAmount,
      refundedAt: row.refundedAt,
      refundCompletedAt: row.refundCompletedAt,
      canCancel: row.canCancel,
      isDowngrade: row.isDowngrade,
    };
  }

  return overlay;
}
